import type {
  AttendanceDashboardListResult,
  AttendanceDashboardQueryParams,
} from '@src/models/interfaces/attendanceDashboard.interface';
import type { RangeFilter } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import { AttendanceDashboardMapper } from '@src/models/mappers/attendanceDashboard.mapper';
import AttendanceDashboardRepository from '@src/repositories/AttendanceDashboardRepository';

const DEFAULT_PAGE_SIZE = 100;
/** Safety cap for the current-day window fetch used by KPIs and journey grouping. */
export const ATTENDANCE_DASHBOARD_MAX_RECORDS_IN_RANGE = 2000;

interface ListScopeParams {
  employeeIds?: string[];
  workJourneyConfigIds?: string[];
  userIds?: string[];
  sort?: AttendanceDashboardQueryParams['sort'];
}

interface ListPageParams extends ListScopeParams {
  organizationId: string;
  range: RangeFilter;
  page: number;
  pageSize?: number;
}

interface ListAllInRangeParams extends ListScopeParams {
  organizationId: string;
  range: RangeFilter;
  pageSize?: number;
}

const buildScopeQueryParams = (params: ListScopeParams): Partial<AttendanceDashboardQueryParams> => ({
  ...(params.employeeIds?.length ? { employeeIds: params.employeeIds } : {}),
  ...(params.workJourneyConfigIds?.length ? { workJourneyConfigIds: params.workJourneyConfigIds } : {}),
  ...(params.userIds?.length ? { userIds: params.userIds } : {}),
  ...(params.sort ? { sort: params.sort } : {}),
});

class AttendanceDashboardService {
  private repository: AttendanceDashboardRepository;

  constructor() {
    this.repository = new AttendanceDashboardRepository();
  }

  async listPage({
    organizationId,
    range,
    page,
    pageSize = DEFAULT_PAGE_SIZE,
    employeeIds,
    workJourneyConfigIds,
    userIds,
    sort,
  }: ListPageParams): Promise<AttendanceDashboardListResult> {
    const lastPosition = Math.max(0, (page - 1) * pageSize);
    const response = await this.repository.query({
      organizationId,
      range,
      pageSize,
      lastPosition,
      ...buildScopeQueryParams({ employeeIds, workJourneyConfigIds, userIds, sort }),
    });

    const pageResult = AttendanceDashboardMapper.fromElasticResponse(response);
    return {
      ...pageResult,
      totalAvailable: pageResult.total,
      truncated: false,
    };
  }

  /**
   * Fetches every hit in the range by paging Elasticsearch.
   * Used for KPI cards and the "Por jornada" tab (client-side grouping).
   * Caps at ATTENDANCE_DASHBOARD_MAX_RECORDS_IN_RANGE and reports truncation.
   */
  async listAllInRange({
    organizationId,
    range,
    pageSize = DEFAULT_PAGE_SIZE,
    workJourneyConfigIds,
    employeeIds,
    userIds,
    sort,
  }: ListAllInRangeParams): Promise<AttendanceDashboardListResult> {
    const aggregatedItems: AttendanceDashboardListResult['items'] = [];
    let lastPosition = 0;
    let totalAvailable = 0;

    while (aggregatedItems.length < ATTENDANCE_DASHBOARD_MAX_RECORDS_IN_RANGE) {
      const response = await this.repository.query({
        organizationId,
        range,
        pageSize,
        lastPosition,
        ...buildScopeQueryParams({ employeeIds, workJourneyConfigIds, userIds, sort }),
      });
      const page = AttendanceDashboardMapper.fromElasticResponse(response);
      totalAvailable = page.total;

      if (page.items.length === 0) {
        break;
      }

      aggregatedItems.push(...page.items);
      lastPosition += page.items.length;

      if (aggregatedItems.length >= totalAvailable) {
        break;
      }
    }

    const items = aggregatedItems.slice(0, ATTENDANCE_DASHBOARD_MAX_RECORDS_IN_RANGE);
    const truncated = totalAvailable > ATTENDANCE_DASHBOARD_MAX_RECORDS_IN_RANGE;

    return {
      items,
      total: items.length,
      totalAvailable,
      truncated,
    };
  }
}

export default AttendanceDashboardService;
